import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useWeb3 } from '../context/Web3Context';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { PageHero } from '../components/PageHero';
import { toast } from 'sonner';
import { Package, Sparkles, ShoppingBag, Store, Tag, X, Zap, TrendingUp, Lock, Flame } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function PackCard({ pack, onAction, actionLabel, secondary, disabled, testid }) {
    const { t } = useTranslation();
    return (
        <Card className="bg-white/5 border-white/10 overflow-hidden hover:border-white/20 transition-all" data-testid={testid}>
            <div className="h-1.5" style={{ backgroundColor: pack.color || '#666' }} />
            <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                    <div>
                        <div className="flex items-center gap-2">
                            <Package className="w-4 h-4" style={{ color: pack.color }} />
                            <h3 className="font-bold text-white text-lg">{pack.label}</h3>
                        </div>
                        <Badge className="mt-1 border-0" style={{ backgroundColor: `${pack.color}22`, color: pack.color }}>
                            {pack.rarity}
                        </Badge>
                    </div>
                    {pack.equipped && (
                        <Badge className="bg-[#00FF66]/20 text-[#00FF66] border-0">{t('boosters.equipped')}</Badge>
                    )}
                </div>

                <div className="space-y-1 mb-4 text-sm text-white/70">
                    <div className="flex items-center gap-2">
                        <Zap className="w-3.5 h-3.5 text-[#FFD700]" />
                        <span>{t('boosters.rewardsForLife', { pct: pack.reward_boost_pct })}</span>
                    </div>
                    {pack.jackpot_ticket_mult > 1.0 && (
                        <div className="flex items-center gap-2">
                            <TrendingUp className="w-3.5 h-3.5 text-[#9B6FE8]" />
                            <span>{t('boosters.jackpotMult', { mult: pack.jackpot_ticket_mult })}</span>
                        </div>
                    )}
                    {pack.fee_discount_pct > 0 && (
                        <div className="flex items-center gap-2">
                            <Sparkles className="w-3.5 h-3.5 text-[#00FF66]" />
                            <span>{t('boosters.feeDiscount', { pct: pack.fee_discount_pct })}</span>
                        </div>
                    )}
                </div>

                {pack.price_b4p != null && (
                    <div className="bg-black/30 border border-white/5 rounded-md p-2.5 mb-3">
                        <div className="text-[10px] uppercase text-white/40">{t('boosters.price')}</div>
                        <div className="font-mono font-bold text-white text-lg">{pack.price_b4p.toLocaleString()} B4P</div>
                        <div className="text-[10px] text-white/40">{t('boosters.priceUsd', { usd: pack.price_usd })}</div>
                    </div>
                )}

                <Button
                    onClick={onAction}
                    disabled={disabled}
                    className="w-full bg-[#FFD700] text-black hover:bg-[#FFD700]/80 font-bold disabled:opacity-50"
                    data-testid={`${testid}-action`}
                >
                    {actionLabel}
                </Button>
                {secondary}
            </CardContent>
        </Card>
    );
}

export default function BoosterPacksPage() {
    const { t } = useTranslation();
    const { address, isConnected } = useWeb3();
    const [tab, setTab] = useState('shop');
    const [catalog, setCatalog] = useState(null);
    const [owned, setOwned] = useState(null);
    const [listings, setListings] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(false);
    const [listingPrices, setListingPrices] = useState({}); // pack_id -> price input

    const refresh = useCallback(async () => {
        try {
            setLoading(true);
            const [c, l, s] = await Promise.all([
                fetch(`${API_URL}/api/booster-packs/catalog`).then(r => r.json()),
                fetch(`${API_URL}/api/booster-packs/marketplace/listings`).then(r => r.json()),
                fetch(`${API_URL}/api/booster-packs/stats`).then(r => r.json()),
            ]);
            setCatalog(c);
            setListings(l?.listings || []);
            setStats(s);
            if (address) {
                const o = await fetch(`${API_URL}/api/booster-packs/owned/${address}`).then(r => r.json());
                setOwned(o);
            }
        } catch (e) {
            console.error(e);
            toast.error(t('boosters.loadError'));
        } finally {
            setLoading(false);
        }
    }, [address, t]);

    useEffect(() => { refresh(); }, [refresh]);

    const buy = async (pack_type) => {
        if (!address) return toast.error(t('boosters.connectWallet'));
        try {
            const r = await fetch(`${API_URL}/api/booster-packs/buy`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ address, pack_type }),
            }).then(r => r.json());
            if (!r.success) return toast.error(r.error || t('boosters.buyFailed'));
            toast.success(t('boosters.buyToast', { type: pack_type, burned: r.burned_b4p }));
            refresh();
        } catch (e) { toast.error(e.message); }
    };

    const equip = async (pack_id) => {
        try {
            const r = await fetch(`${API_URL}/api/booster-packs/equip`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ address, pack_id }),
            }).then(r => r.json());
            if (!r.success) return toast.error(r.error);
            toast.success(t('boosters.equipToast'));
            refresh();
        } catch (e) { toast.error(e.message); }
    };

    const unequip = async (pack_id) => {
        const r = await fetch(`${API_URL}/api/booster-packs/unequip`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ address, pack_id }),
        }).then(r => r.json());
        if (!r.success) return toast.error(r.error);
        toast.success(t('boosters.unequipToast'));
        refresh();
    };

    const listForSale = async (pack_id) => {
        const price = parseFloat(listingPrices[pack_id]);
        if (!price || price <= 0) return toast.error(t('boosters.invalidPrice'));
        const r = await fetch(`${API_URL}/api/booster-packs/marketplace/list`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ address, pack_id, price_b4p: price }),
        }).then(r => r.json());
        if (!r.success) return toast.error(r.error);
        toast.success(t('boosters.listedToast', { price }));
        setListingPrices(p => ({ ...p, [pack_id]: '' }));
        refresh();
    };

    const buyListing = async (listing_id) => {
        if (!address) return toast.error(t('boosters.connectWallet'));
        const r = await fetch(`${API_URL}/api/booster-packs/marketplace/buy`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ address, listing_id }),
        }).then(r => r.json());
        if (!r.success) return toast.error(r.error);
        toast.success(t('boosters.boughtToast', { price: r.price_b4p, royalty: r.royalty_b4p }));
        refresh();
    };

    const cancelListing = async (listing_id) => {
        const r = await fetch(`${API_URL}/api/booster-packs/marketplace/cancel`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ address, listing_id }),
        }).then(r => r.json());
        if (!r.success) return toast.error(r.error);
        toast.success(t('boosters.cancelledToast'));
        refresh();
    };

    return (
        <div data-testid="booster-packs-page">
            <PageHero
                icon={Package}
                title={t('boosters.title')}
                highlight={t('boosters.highlight')}
                subtitle={t('boosters.subtitle')}
            />
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">

            {/* Active Boost Banner */}
            {owned?.active_boost?.pack && (
                <Card className="bg-gradient-to-r from-[#FFD700]/10 to-[#FF4500]/10 border-[#FFD700]/30 mb-6" data-testid="active-boost-banner">
                    <CardContent className="p-4 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <Zap className="w-6 h-6 text-[#FFD700]" />
                            <div>
                                <div className="font-bold text-white">{t('boosters.activeBoost', { pack: owned.active_boost.pack.label })}</div>
                                <div className="text-xs text-white/60">
                                    {t('boosters.rewardsForLife', { pct: owned.active_boost.reward_boost_pct })}
                                    {owned.active_boost.jackpot_ticket_mult > 1.0 && ` · ${t('boosters.jackpotMult', { mult: owned.active_boost.jackpot_ticket_mult })}`}
                                    {owned.active_boost.fee_discount_pct > 0 && ` · ${t('boosters.feeDiscount', { pct: owned.active_boost.fee_discount_pct })}`}
                                </div>
                            </div>
                        </div>
                        <Badge className="bg-[#00FF66]/20 text-[#00FF66] border-0">{t('boosters.activeBoostBadge')}</Badge>
                    </CardContent>
                </Card>
            )}

            {/* Stats Strip */}
            {stats && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8" data-testid="booster-stats">
                    <Card className="bg-white/5 border-white/10">
                        <CardContent className="p-3">
                            <div className="text-[10px] uppercase text-white/40">{t('boosters.packsMinted')}</div>
                            <div className="font-mono text-xl font-bold text-white">{stats.total_packs_minted}</div>
                        </CardContent>
                    </Card>
                    <Card className="bg-white/5 border-white/10">
                        <CardContent className="p-3">
                            <div className="text-[10px] uppercase text-white/40">{t('boosters.shopVolume')}</div>
                            <div className="font-mono text-xl font-bold text-white">{Number(stats.shop_volume_b4p).toLocaleString()} B4P</div>
                            <div className="text-[10px] text-white/40 flex items-center gap-1">
                                <Flame className="w-3 h-3 text-[#FF4500]" />
                                {stats.shop_burned_b4p?.toLocaleString()} {t('boosters.burned')}
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="bg-white/5 border-white/10">
                        <CardContent className="p-3">
                            <div className="text-[10px] uppercase text-white/40">{t('boosters.marketplaceVolume')}</div>
                            <div className="font-mono text-xl font-bold text-white">{Number(stats.marketplace_volume_b4p).toLocaleString()} B4P</div>
                            <div className="text-[10px] text-white/40">{t('boosters.sales', { count: stats.marketplace_sale_count })}</div>
                        </CardContent>
                    </Card>
                    <Card className="bg-white/5 border-white/10">
                        <CardContent className="p-3">
                            <div className="text-[10px] uppercase text-white/40">{t('boosters.royaltiesEarned')}</div>
                            <div className="font-mono text-xl font-bold text-[#00FF66]">{Number(stats.marketplace_royalty_b4p).toLocaleString()} B4P</div>
                        </CardContent>
                    </Card>
                </div>
            )}

            <Tabs value={tab} onValueChange={setTab} className="w-full">
                <TabsList className="bg-white/5 border-white/10 mb-6">
                    <TabsTrigger value="shop" data-testid="tab-shop"><ShoppingBag className="w-4 h-4 mr-2" />{t('boosters.shopTab')}</TabsTrigger>
                    <TabsTrigger value="my-packs" data-testid="tab-my-packs"><Package className="w-4 h-4 mr-2" />{t('boosters.myPacksTab')}</TabsTrigger>
                    <TabsTrigger value="marketplace" data-testid="tab-marketplace"><Store className="w-4 h-4 mr-2" />{t('boosters.marketplaceTab')}</TabsTrigger>
                </TabsList>

                <TabsContent value="shop">
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="shop-grid">
                        {catalog?.packs?.map(p => (
                            <PackCard
                                key={p.type}
                                pack={p}
                                onAction={() => buy(p.type)}
                                actionLabel={isConnected ? t('boosters.buy') : t('common.connectWallet')}
                                disabled={!isConnected || loading}
                                testid={`shop-pack-${p.type}`}
                            />
                        ))}
                    </div>
                </TabsContent>

                <TabsContent value="my-packs">
                    {!isConnected ? (
                        <Card className="bg-white/5 border-white/10">
                            <CardContent className="py-12 text-center">
                                <Lock className="w-10 h-10 mx-auto text-white/30 mb-3" />
                                <p className="text-white/60">{t('boosters.connectToSeePacks')}</p>
                            </CardContent>
                        </Card>
                    ) : !owned?.packs?.length ? (
                        <Card className="bg-white/5 border-white/10">
                            <CardContent className="py-12 text-center">
                                <Package className="w-10 h-10 mx-auto text-white/30 mb-3" />
                                <p className="text-white/60">{t('boosters.noPacksOwnedTitle')}</p>
                            </CardContent>
                        </Card>
                    ) : (
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="my-packs-grid">
                            {owned.packs.map(p => (
                                <PackCard
                                    key={p.pack_id}
                                    pack={p}
                                    onAction={() => p.equipped ? unequip(p.pack_id) : equip(p.pack_id)}
                                    actionLabel={p.equipped ? 'Retirer' : 'Équiper'}
                                    testid={`owned-pack-${p.pack_id}`}
                                    secondary={
                                        <div className="mt-2 space-y-1.5">
                                            <Input
                                                type="number"
                                                placeholder={t('boosters.pricePlaceholder')}
                                                value={listingPrices[p.pack_id] || ''}
                                                onChange={(e) => setListingPrices(prev => ({ ...prev, [p.pack_id]: e.target.value }))}
                                                className="bg-black/40 border-white/10 text-white text-sm h-9"
                                                data-testid={`list-price-${p.pack_id}`}
                                            />
                                            <Button
                                                onClick={() => listForSale(p.pack_id)}
                                                variant="outline"
                                                className="w-full border-white/20 text-white hover:bg-white/5 text-sm h-9"
                                                data-testid={`list-btn-${p.pack_id}`}
                                            >
                                                <Tag className="w-3.5 h-3.5 mr-2" />
                                                {t('boosters.listOnMarketplace')}
                                            </Button>
                                        </div>
                                    }
                                />
                            ))}
                        </div>
                    )}
                </TabsContent>

                <TabsContent value="marketplace">
                    {!listings.length ? (
                        <Card className="bg-white/5 border-white/10">
                            <CardContent className="py-12 text-center">
                                <Store className="w-10 h-10 mx-auto text-white/30 mb-3" />
                                <p className="text-white/60">{t('boosters.noListings')}</p>
                            </CardContent>
                        </Card>
                    ) : (
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="marketplace-grid">
                            {listings.map(l => {
                                const isMine = address && l.seller?.toLowerCase() === address.toLowerCase();
                                const pack = l.pack || {};
                                return (
                                    <Card key={l.listing_id} className="bg-white/5 border-white/10" data-testid={`listing-${l.listing_id}`}>
                                        <div className="h-1.5" style={{ backgroundColor: pack.color || '#666' }} />
                                        <CardContent className="p-4">
                                            <div className="flex items-start justify-between mb-2">
                                                <h3 className="font-bold text-white">{pack.label || l.pack_type}</h3>
                                                <Badge className="border-0" style={{ backgroundColor: `${pack.color}22`, color: pack.color }}>
                                                    {pack.rarity}
                                                </Badge>
                                            </div>
                                            <div className="text-xs text-white/60 mb-2">{t('boosters.rewardBoostShort', { pct: pack.reward_boost_pct })}</div>
                                            <div className="bg-black/30 rounded-md p-2 mb-3">
                                                <div className="text-[10px] uppercase text-white/40">{t('boosters.askingPrice')}</div>
                                                <div className="font-mono font-bold text-white">{Number(l.price_b4p).toLocaleString()} B4P</div>
                                            </div>
                                            <div className="text-[10px] text-white/40 mb-3 truncate">
                                                {t('boosters.seller')} {l.seller?.slice(0, 6)}...{l.seller?.slice(-4)}
                                            </div>
                                            {isMine ? (
                                                <Button
                                                    onClick={() => cancelListing(l.listing_id)}
                                                    variant="outline"
                                                    className="w-full border-red-500/30 text-red-400 hover:bg-red-500/10"
                                                    data-testid={`cancel-listing-${l.listing_id}`}
                                                >
                                                    <X className="w-3.5 h-3.5 mr-2" />
                                                    {t('boosters.cancelListing')}
                                                </Button>
                                            ) : (
                                                <Button
                                                    onClick={() => buyListing(l.listing_id)}
                                                    disabled={!isConnected}
                                                    className="w-full bg-[#FFD700] text-black hover:bg-[#FFD700]/80 font-bold"
                                                    data-testid={`buy-listing-${l.listing_id}`}
                                                >
                                                    {t('boosters.buy')}
                                                </Button>
                                            )}
                                        </CardContent>
                                    </Card>
                                );
                            })}
                        </div>
                    )}
                </TabsContent>
            </Tabs>
            </div>
        </div>
    );
}
